import React from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translator } from 'mage-i18n';
import _cloneDeep from 'lodash/cloneDeep';
import _every from 'lodash/every';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _omit from 'lodash/omit';

import { SpacerVertical, SpacerHorizontal, SwitchCheckbox, Typography } from '@klarna/bubble-ui';
import {
  AddMoreIfAllowed,
  defaultContainerPropTypes,
  HorizontalWrapper,
  moetPropTypes,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import { CY_STAKEHOLDER_CONTAINER_IDS } from '@/constants/stakeholders';
import {
  getMerchantAwareTranslator,
  getMerchantCountryKey,
  merchantRegion,
} from '@/selectors/merchant';
import { getStakeholders } from '@/selectors/merchant/recollection';

import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  StakeholderFormCard,
} from '@/components';
import { STAKEHOLDER_ROLES_V2, stakeholderUtils } from '@/containers/withStakeholderLogic';
import { withRecollectionStakeholderLogic } from '../withRecollectionStakeholderLogic';
import { getDefaultState, getSubmittedApplicantIds } from './selectors';

const tPrefix = 'products-fe.containers.applicant';

export class Applicant extends React.Component {
  constructor(props) {
    super(props);

    const { defaultState, submittedApplicantIds = [] } = props;
    const stakeholdersDataFromState = defaultState.stakeholders.map(stakeholder => {
      return {
        ...stakeholder,
        isChecked: submittedApplicantIds.includes(stakeholder.id),
      };
    });

    this.state = {
      stakeholdersDataFromState,
      ctaClicked: false,
    };

    this.onChange = this.onChange.bind(this);
    this.onNext = this.onNext.bind(this);
    this.getBody = this.getBody.bind(this);
  }

  getStakeholderDataString(stakeholder) {
    const stakeholderWithoutAddressCountry = _omit(stakeholder, 'address.country');
    const addressWithoutCountry = _get(stakeholderWithoutAddressCountry, 'address');
    const hasNoAddress = _every(addressWithoutCountry, _isEmpty);

    let label = `${stakeholder.firstName} ${stakeholder.lastName}, ${stakeholder.address.street},
    ${stakeholder.address.postalCode} ${stakeholder.address.city}`;
    if (hasNoAddress) label = `${stakeholder.firstName} ${stakeholder.lastName}`;

    return label;
  }

  onChange(val, id) {
    const { stakeholdersDataFromState } = this.state;
    const updatedStakeholders = _cloneDeep(stakeholdersDataFromState);
    const selectedIndex = updatedStakeholders.findIndex(s => s.id === id);

    if (selectedIndex !== -1) {
      updatedStakeholders[selectedIndex].isChecked = val;
    }

    this.setState({ stakeholdersDataFromState: updatedStakeholders });
  }

  getStakeholdersFromSelector() {
    const { allStakeholders } = this.props;
    const { stakeholdersDataFromState } = this.state;
    const isApplicant = id =>
      stakeholdersDataFromState.find(st => st.id === id && st.isChecked) !== undefined;

    return allStakeholders
      .map(stakeholder => {
        if (isApplicant(stakeholder.id)) {
          return {
            ...stakeholder,
            roles: Array.from(new Set([...stakeholder.roles, STAKEHOLDER_ROLES_V2.APPLICANT])),
          };
        }
        return {
          ...stakeholder,
          roles: stakeholder.roles.filter(role => role !== STAKEHOLDER_ROLES_V2.APPLICANT),
        };
      })
      .filter(stakeholder => stakeholder.roles.length);
  }

  hasAtleastOneApplicant() {
    return (
      this.state.stakeholdersDataFromState.some(stakeholder => stakeholder.isChecked) ||
      this.props.stakeholders.some(
        stakeholder =>
          stakeholder.roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT) &&
          stakeholder.roles.length === 1,
      )
    );
  }

  getAddedApplicants() {
    return this.props.stakeholders.filter(
      s => s.roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT) && s.roles.length === 1,
    );
  }

  getBody() {
    const { mid } = this.props;
    const addedStakeholders = this.getAddedApplicants();
    const selectedStakeholders = this.getStakeholdersFromSelector();

    const body = [
      ...selectedStakeholders,
      ...stakeholderUtils.getStakeholdersMappedValues(addedStakeholders),
    ];

    return { payload: { params: { mid }, body } };
  }

  onNext() {
    const { onNextWrapper, stakeholders } = this.props;

    this.setState({ ctaClicked: true });

    const isDataValid =
      stakeholderUtils.isStakeholdersValid(stakeholders) && this.hasAtleastOneApplicant();

    onNextWrapper(this.getBody(), isDataValid);
  }

  render() {
    const {
      availableRoles = [],
      currentStakeholdersCount,
      error,
      hasBack,
      isDisabled,
      isLoading,
      max,
      min,
      onAddStakeholder,
      onBack,
      onBlurStakeholder,
      onChangeStakeholder,
      onTouchPersonalId,
      onRemoveStakeholder,
      roleConstraints,
      stakeholders,
      t,
      tMerchantAware,
    } = this.props;
    const { stakeholdersDataFromState, ctaClicked } = this.state;

    const hasApplicant = this.hasAtleastOneApplicant();
    const infoMessage =
      ctaClicked && !hasApplicant
        ? `products-fe.error_messages.validation.generic_toggle_undefined`
        : `${tPrefix}.robot_message`;

    const addedApplicants = this.getAddedApplicants();
    const tabIndex =
      stakeholdersDataFromState.length +
      8 * stakeholders.length +
      (stakeholders.length > min ? 1 : 0) +
      1;

    const allowToRemove =
      currentStakeholdersCount === 0 ? stakeholders.length > min : stakeholders.length >= min;

    // do not filter stakeholders directly because the whole logic is based on stakeholder's index
    // and by filtering stakeholder's index will be changed
    const stakeholdersToAdd = stakeholders.map((stakeholder, index) => {
      if (
        stakeholder.roles.includes(STAKEHOLDER_ROLES_V2.APPLICANT) &&
        stakeholder.roles.length === 1
      ) {
        return (
          <React.Fragment key={`applicant-${stakeholder.reactFormId}`}>
            <StakeholderFormCard
              autoFocus={index === 0}
              tabIndexStart={
                stakeholdersDataFromState.length +
                index * (8 + (stakeholders.length > min ? 1 : 0)) +
                1
              }
              stakeholder={stakeholder}
              availableRoles={availableRoles}
              disabledRoles={stakeholderUtils.getDisabledRoles(
                roleConstraints,
                stakeholders,
                stakeholder.roles,
              )}
              onBlur={key => onBlurStakeholder(index, key)}
              onChange={(key, value) => onChangeStakeholder(index, key, value)}
              onTouchPersonalId={() => onTouchPersonalId(index)}
              onRemove={allowToRemove ? () => onRemoveStakeholder(index) : undefined}
              isDisabled={isDisabled}
              name={`applicant-${index}`}
            />
            <SpacerVertical small />
          </React.Fragment>
        );
      } else return null;
    });

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title.recollection`)}
        subtitle={t(`${tPrefix}.subtitle.recollection`)}
        error={error}
        info={tMerchantAware(infoMessage)}
      >
        {stakeholdersDataFromState.map(stakeholder => (
          <React.Fragment key={stakeholder.id}>
            <SwitchCheckbox
              name={`applicant-${stakeholder.id}`}
              disabled={isDisabled}
              checked={stakeholder.isChecked}
              onChange={val => this.onChange(val, stakeholder.id)}
            >
              {this.getStakeholderDataString(stakeholder)}
            </SwitchCheckbox>
            <SpacerVertical small />
          </React.Fragment>
        ))}
        <SpacerVertical small />
        {addedApplicants.length > 0 && (
          <>
            <Typography textToken="text-style/headings/titles/bold/grande">
              {t(`${tPrefix}.form_title`)}
            </Typography>
            <SpacerVertical medium />
          </>
        )}
        {stakeholdersToAdd}
        <AddMoreIfAllowed
          tabIndex={tabIndex}
          onClick={onAddStakeholder}
          text={t(`${tPrefix}.buttons.add_person`)}
          currentCount={stakeholders.length}
          maxCount={max}
          disabled={isDisabled}
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton
                tabIndex={tabIndex + 2}
                onClick={onBack}
                disabled={isDisabled}
                {...generateCyElemId(CY_STAKEHOLDER_CONTAINER_IDS.BTN_BACK, window)}
              >
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            tabIndex={tabIndex + 1}
            onClick={this.onNext}
            loading={isLoading}
            disabled={isDisabled}
            {...generateCyElemId(CY_STAKEHOLDER_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

Applicant.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  allStakeholders: PropTypes.array.isRequired, // all listed stakeholders
  currentStakeholdersCount: PropTypes.number.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  region: PropTypes.string.isRequired,
  stakeholders: PropTypes.array.isRequired, // current stakeholder
  t: PropTypes.func.isRequired,
  onBlurStakeholder: PropTypes.func.isRequired,
  onChangeStakeholder: PropTypes.func.isRequired,
  submittedApplicantIds: PropTypes.array,
};

export default compose(
  connect(state => ({
    merchantCountry: getMerchantCountryKey(state),
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    defaultState: getDefaultState(state),
    submittedApplicantIds: getSubmittedApplicantIds(state),
    allStakeholders: getStakeholders(state),
    region: merchantRegion(state),
    currentStakeholdersCount: getStakeholders(state).length,
  })),
  withRecollectionStakeholderLogic(CAPS.V2_MERCHANTS_RECOLLECTION_PUT),
)(Applicant);
